import { Link } from '@tanstack/react-router';
import type { KnowledgeDetailResponse } from '../lib/api';

/**
 * Knowledge page.
 *
 * The entity is explained before any evidence is listed, and every displayed
 * relationship says how much evidence stands behind it and where that evidence is.
 * Citation counts appear as a labelled derived metric, never as a bare number implying
 * importance or correctness.
 */
export function KnowledgePage({ detail }: { detail: KnowledgeDetailResponse }) {
  const citation = detail.citation;

  return (
    <div className="reading-container">
      <header className="doc-header">
        <div className="breadcrumbs">Knowledge / {detail.node.kind}</div>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }} aria-hidden="true">
            🧠
          </span>
          {detail.node.title}
        </h1>
        <div className="doc-meta">
          <span className="badge concept-badge">{detail.node.kind}</span>
          <span className="meta-item">
            Canonical entity ID: <a href={detail.node.canonicalUrl}>{detail.node.id}</a>
          </span>
          {detail.node.curated ? <span className="badge status-latest">Curated</span> : null}
        </div>
      </header>

      <article className="markdown-body">
        {detail.node.summary ? (
          <p className="lead" style={{ fontSize: 16, color: 'var(--text-muted)', marginBottom: 24 }}>
            {detail.node.summary}
          </p>
        ) : (
          <p className="lead" style={{ color: 'var(--text-muted)' }}>
            No sourced summary has been admitted for this entity yet. Its evidence is listed below.
          </p>
        )}

        {citation ? (
          <div className="citation-panel">
            <details className="citation-indicator">
              <summary>
                <div className="citation-summary">
                  <span className="cite-icon" aria-hidden="true">
                    🔗
                  </span>
                  <strong>Citation provenance:</strong> cited in {citation.distinctCitingClaims} claims across{' '}
                  {citation.distinctCitingDocuments} documents and {citation.distinctCitingRepositories} repositories
                </div>
              </summary>
              <div className="citation-breakdown">
                <h4>Breakdown</h4>
                <ul>
                  <li>Declared citations: {citation.declaredCitations}</li>
                  <li>Deterministically extracted citations: {citation.extractedCitations}</li>
                  <li>Same-repository citations: {citation.sameRepositoryCitations}</li>
                  <li>
                    Influence score:{' '}
                    {citation.influenceScore === null
                      ? 'not published — a composite score ships only with its formula and fixtures'
                      : citation.influenceScore}
                  </li>
                </ul>
                <div className="citation-footer">
                  <span>
                    Algorithm: <code>{citation.algorithm}@{citation.algorithmVersion}</code>
                  </span>
                  <span>Generation: <code>{citation.graphGeneration}</code></span>
                </div>
                <p className="text-small" style={{ marginTop: 12 }}>
                  This is a derived metric over the active generation. It measures how often this entity is
                  referenced — not whether it is correct, authoritative, or approved. Keyword occurrence and
                  contribution attribution are excluded.
                </p>
              </div>
            </details>
          </div>
        ) : null}

        {detail.claims.length > 0 ? (
          <>
            <h2 id="claims">Claims</h2>
            {detail.claims.map((claim) => (
              <div key={claim.id} className="evidence-item-large" style={{ marginBottom: 16 }}>
                <h4>
                  {claim.statement} <span className="ev-badge">{claim.origin}</span>
                </h4>
                <details className="evidence-details">
                  <summary>
                    {claim.evidence.length} supporting {claim.evidence.length === 1 ? 'section' : 'sections'}
                  </summary>
                  <div className="evidence-list">
                    {claim.evidence.map((evidence) => (
                      <EvidenceRow key={evidence.sectionId} evidence={evidence} />
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </>
        ) : null}

        {detail.relationships.length > 0 || detail.backlinks.length > 0 ? (
          <>
            <h2 id="relationships">Key relationships</h2>
            <ul className="neighbor-list">
              {detail.relationships.map((relation) => (
                <li key={`out:${relation.node.id}:${relation.type}`}>
                  <Link to={relation.node.canonicalUrl}>{relation.node.title}</Link>
                  <span className="relation">
                    {relation.type} · {relation.origin} · {relation.evidenceCount} evidence
                  </span>
                </li>
              ))}
              {detail.backlinks.map((relation) => (
                <li key={`in:${relation.node.id}:${relation.type}`}>
                  <Link to={relation.node.canonicalUrl}>{relation.node.title}</Link>
                  <span className="relation">
                    referenced by · {relation.type} · {relation.evidenceCount} evidence
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <h2 id="evidence">Document evidence</h2>
        {detail.evidence.length === 0 ? (
          <p className="text-small">No admitted document section cites this entity in the active generation.</p>
        ) : (
          <div className="evidence-list-large">
            {detail.evidence.map((evidence) => (
              <div className="evidence-item-large" key={evidence.sectionId}>
                <h4>
                  <a href={evidence.canonicalUrl}>{evidence.title}</a>
                  <span className="badge status-latest">Latest</span>
                </h4>
                <div className="ev-path">
                  {evidence.sourcePath}
                  {evidence.sectionAnchor ? `#${evidence.sectionAnchor}` : ''}
                </div>
                {evidence.snippet ? <blockquote className="ev-snippet">{evidence.snippet}</blockquote> : null}
                <div className="cite-meta">
                  <a href={evidence.canonicalUrl}>Latest</a>
                  {evidence.immutableUrl ? <a href={evidence.immutableUrl}>At producing revision</a> : null}
                  {evidence.contentDigest ? (
                    <span className="cite-rev">digest {evidence.contentDigest.slice(0, 12)}…</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}

function EvidenceRow({ evidence }: { evidence: KnowledgeDetailResponse['evidence'][number] }) {
  return (
    <div className="evidence-item">
      <div className="ev-title">
        <a href={evidence.canonicalUrl}>{evidence.title}</a> <span className="ev-badge">DocumentSection</span>
      </div>
      <div className="ev-path">
        {evidence.sourcePath}
        {evidence.sectionAnchor ? `#${evidence.sectionAnchor}` : ''}
      </div>
      {evidence.snippet ? <div className="ev-snippet">{evidence.snippet}</div> : null}
    </div>
  );
}
